import { inject, Injectable, NgZone } from '@angular/core';
import { Row, Table } from 'src/app/models/ui/table';
import { AppDB, OPRUEF } from './dexieDB';
import {
  AsAlias,
  AsMappedRow,
  AsMappedTableFE,
  AsTableFE,
  GetDBStatus,
  GetKundenBezFor,
  MappedColumns,
  SharedDB,
} from './sharedDB';
import { LogEintrag } from 'src/app/content/_components/detailview/mangel-log-modal/mangel-log-modal.component';
import { DBStatus, Media, MRproResponseInfo } from 'src/app/types/api';
import {
  MangelModel,
  MangelRowModel,
  MangelRowType,
} from 'src/app/content/_components/_overlays/mangelerfassungsdialog/mangel-erfassung.types';
import { FileObject } from '../base64/base64.service';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { GetLPDatumFieldBy, GetLPIDFieldBy, OSTAMM, SOTYP } from './anlagenDB';
import { Aliased } from 'src/app/types/shared';
import { ObjTypePipe } from 'src/app/pipes/objtype.pipe';
import { EinstellungenDB } from './einstellungenDB';
import { distinct, insertBeforeBy, lookupBy, sort, sortBy } from 'src/app/misc/functions';
import { compareDate } from 'src/app/pipes/compare-date.pipe';
import { IsMangelValid } from 'src/app/content/_components/_overlays/mangelerfassungsdialog/mangeldialog.component';
import Dexie from 'dexie';
import { BaseItem } from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-wrapper.component';

@Injectable({ providedIn: 'root' })
export class MangelDB {
  private readonly zone = inject(NgZone);
  private readonly db = inject(AppDB);
  private readonly einstellungenDB = inject(EinstellungenDB);
  private readonly sharedDB = inject(SharedDB);

  async GetMaengel(alle: boolean, KPRUVID: number): Promise<{ all: Table; wartung: Table; stoerung: Table }> {
    return await this.zone.runOutsideAngular(async () => {
      try {
        const kbez = await this.sharedDB.GetKundenBezeichnungColumnNames();
        const LPIDField = GetLPIDFieldBy(KPRUVID);

        const cols: MappedColumns<
          OSTAMM & Aliased<SOTYP, 'sotyp'> & Aliased<OPRUEF, 'opruef'> & Aliased<OMAZS, 'omazs'>
        > = [
          ['omazs.OPRUEFID', 'opruefid'],
          ['omazs.OMAZSID', 'omazsid'],
          ['OTYPID', 'h1'],
          ['sotyp.Bezeichnung', 'typ'],
          ['sotyp.SysIcon', 'typ_icon'],
          ['sotyp.Typ', 'otypid'],
          ['omazs.OMARTID', 'omartid'],
          ['SperrID', 'SPID'],
          ['SperrStatus', 'SP'],
          'Richtung',
          ['LFmin', 'lfstatus'],
          ['LPID', 'lpid'],
          ['Bezeichnung', 'anlagenobjekte'],
          ...GetKundenBezFor(['Ort', 'Bezirk', 'Stellwerk'], kbez),
          ['MessRegBezirk', 'messreg_bezirk'],
          ['Startpunkt', 'startpunkt'],
          ['Endpunkt', 'endpunkt'],
          ['omazs.StartMeter', 'startmeter'],
          ['omazs.EndMeter', 'endmeter'],
          ...GetKundenBezFor(['Anlagenklasse'], kbez),
          ['opruef.P_Datum', 'pruefdatum'],
          ['omazs.Gattung', 'gattung'],
          ['omazs.Stamm', 'stamm'],
          ['omazs.Pruefart', 'pruefart'],
          ['omazs.Mangel', 'mangel'],
          ['omazs.Lage', 'lage'],
          ['omazs.Umfang', 'umfang'],
          ['omazs.Mengeneinheit', 'mengeneinheit'],
          ['omazs.FK', 'fehlerklasse'],
          ['omazs.Instandsetzung', 'instandsetzung'],
          ['omazs.Betriebsgefahr', 'betriebsgefahr'],
          ['omazs.Bild', 'mangelfoto'],
          ['omazs.InstPic1', 'rueckbilder'],
          ['omazs.EP', 'ep'],
          ['omazs.Bemerkung', 'bemerkung'],
          ...GetKundenBezFor(['Messfile'], kbez),
          ['omazs.Termin', 'termin'],
          ['omazs.Auftrag', 'auftrag'],
          ['omazs.AuftragNr', 'auftrag_nr'],
          ['omazs.Auftragnehmer', 'auftragnehmer'],
          ['omazs.Auftragsart', 'auftragsart'],
          ['omazs.Erledigt', 'erledigt'],
          ['omazs.E_Datum', 'erledigt_am'],
          ['omazs.E_Firma', 'erledigt_durch'],
          ['omazs.Kosten', 'kosten'],
          ['omazs.Kostenart', 'kostenart'],
          ['omazs.MELDNR', 'sapmelungnr'],
          ['omazs.MELDSTAT', 'sapmeldungstatus'],
          ['omazs.AUFSTAT', 'sapauftragstatus'],
          ['omazs.SAPEckende', 'sapgewende'],
          ['omazs.SAPEckendeNeu', 'sapgewendeneu'],
          ['omazs.SAPAusfall', 'ausfall_droht'],
          ['omazs.zuAbt', 'zuständige_abteilung'],
          ['omazs.S', 's'],
          ['omazs.V', 'v'],
          ['omazs.D', 'd'],
          ['omazs.IntAuftragNr', 'intauftragnr'],
          'Laenge',
          ...GetKundenBezFor(['PlanNr', 'FremdID', 'Eigentuemer', 'Kostenstelle'], kbez),
          ['omazs.LVPos', 'lv_pos'],
          ['omazs.UserName', 'username'],
          ['omazs.Status', 'status'],
        ];

        const objekte = await this.db.ostamm.toArray();

        const pruefungen = await Promise.all(
          objekte.map(async objekt => {
            const sotyp = AsAlias(await this.db.sotyp.get(objekt.OTYPID), 'sotyp');
            if (!objekt.LFaktiv) objekt.LFmin = null;
            if (!objekt[LPIDField]) return [];
            const inspektion = alle ? { OSTAMMID: objekt.OSTAMMID } : { OPRUEFID: objekt[LPIDField] };
            const pruefungen = await this.db.opruef
              .where(inspektion)
              .and(x => x.KPRTYPID == KPRUVID)
              .toArray();

            return pruefungen.map(pruefung => ({ ...objekt, ...sotyp, ...AsAlias(pruefung, 'opruef') }));
          })
        );

        const maengel = await Promise.all(
          pruefungen.flat().map(async pruefung => {
            const maengel = await this.db.omazs.where({ OPRUEFID: pruefung['opruef.OPRUEFID'] }).toArray();
            return maengel
              .filter(mangel => !!mangel.Instandsetzung)
              .map(mangel => ({ ...pruefung, ...AsAlias(mangel, 'omazs') }));
          })
        );

        const rows = maengel.flat().sort((a, b) => b['omazs.Status'].localeCompare(a['omazs.Status']));
        const table = AsMappedTableFE()(rows, cols);

        return { all: table, stoerung: null, wartung: null };
      } catch (_) {
        return null;
      }
    });
  }

  async GetMangelLog(OMAZSID: number): Promise<Table<LogEintrag>> {
    const logs = await this.db.omzlog.where({ OMAZSID }).toArray();
    return AsTableFE(logs);
  }

  async GetChecklistenTitels(OTYPID: number): Promise<string[]> {
    const items = await this.db.kstpvg.where({ OTYPID }).toArray();
    return sort(distinct(items, 'Titel'));
  }

  async SaveChecklist(Titel: string, OTYPID: number, items: BaseItem[]) {
    await this.db.transaction('rw', [this.db.sprue, this.db.kstpvg], async () => {
      let sortID = 100_000;
      for (let { ID, Bezeichnung } of items) {
        ID ??= await this.db.sprue.add({ Bezeichnung, OTYPID, ...GetDBStatus() });
        await this.db.kstpvg.add({ OTYPID, PruefartID: ID, Titel, SortID: sortID++, ...GetDBStatus() });
      }
    });
  }

  async GetOrCreateInspektion(args: {
    OSTAMMID: number;
    OPRUEFID: number;
    OTYPID: number;
    KPRUVID: number;
    typ_Line_Point: number;
    isEdit: boolean;
  }): Promise<{ OPRUEFID: number; isMessungDrehen: boolean; Datum: string }> {
    return await this.zone.runOutsideAngular(async () => {
      const isChecklist = await this.einstellungenDB.GetTypOption(args.OTYPID, 'Opt_I_CheckList');
      const OPRUEFID = await this.db.transaction(
        'rw',
        [this.db.opruef, this.db.omazs, this.db.omzlog, this.db.ostamm],
        async () => {
          const { opruef, vonHeute } = await this.IsInspektionVonHeute(args.OPRUEFID);
          let OPRUEFID =
            args.isEdit || vonHeute ?
              opruef.OPRUEFID
            : await this.createNewPruefung(
                args.OSTAMMID,
                opruef,
                args.KPRUVID,
                args.OTYPID,
                args.typ_Line_Point,
                !isChecklist
              );

          return OPRUEFID;
        }
      );

      const opruef = await this.db.opruef.where({ OPRUEFID }).first();

      return { OPRUEFID, isMessungDrehen: opruef.M_Mess_Drehen, Datum: opruef.P_Datum };
    });
  }

  async SaveInspektionsDetails({
    OPRUEFID,
    OSTAMMID,
    KPRUVID,
    P_Datum,
    M_Mess_Drehen,
  }: {
    OPRUEFID: number;
    OSTAMMID: number;
    KPRUVID: number;
    P_Datum: string;
    M_Mess_Drehen: boolean;
  }) {
    await this.db.transaction('rw', [this.db.opruef, this.db.ostamm], async () => {
      const LPDatumField = GetLPDatumFieldBy(KPRUVID);
      await this.db.opruef.where({ OPRUEFID }).modify({ P_Datum, M_Mess_Drehen });
      await this.db.ostamm.where({ OSTAMMID }).modify({ [LPDatumField]: P_Datum });
    });
  }

  async SetInspektionsDelStatus({
    OPRUEFID,
    OSTAMMID,
    KPRUVID,
  }: {
    OPRUEFID: number;
    OSTAMMID: number;
    KPRUVID: number;
  }) {
    await this.db.transaction('rw', [this.db.opruef, this.db.ostamm], async () => {
      await this.db.opruef.where({ OPRUEFID }).modify({ P_DelStatus: 1 });
      await this.resetLPID(OSTAMMID, KPRUVID);
    });
  }

  async GetMaengelInInspektion(args: {
    OPRUEFID: number;
    OSTAMMID: number;
    OTYPID: number;
    typ_Line_Point: number;
  }): Promise<Table<MangelRowType>> {
    return await this.getMaengel(args);
  }

  async SaveMangelToHistorie(mangel: MangelModel, files: FileObject[]): Promise<Row> {
    const OMAZSID = await this.updateMangel(mangel, files);
    return await this.getInHistorie(OMAZSID);
  }

  async UpdateStatus(OMAZSID: number) {
    await this.db.omazs.where({ OMAZSID }).modify({ ...GetDBStatus() });
  }

  async UpdateSZUMZID(OMAZSID: number, SZUMZID: number) {
    await this.db.omazs.where({ OMAZSID }).modify({ SZUMZID });
  }

  async UpdateLfNr(OMAZSID: number, lfNr: number) {
    await this.db.omazs.where({ OMAZSID }).modify({ lfNr });
  }

  async UpdateToEmpty(req: {
    OTYPID: number;
    OSTAMMID: number;
    OPRUEFID: number;
    typ_Line_Point: number;
    OMAZSIDs: number[];
  }): Promise<Table<MangelRowType>> {
    await this.db.omazs
      .where('OMAZSID')
      .anyOf(req.OMAZSIDs)
      .modify({
        ...EMPTY_MANGEL,
        ...GetDBStatus(),
      });

    return this.getMaengel(req);

    /* const maengel = await this.db.omazs.where('OMAZSID').anyOf(OMAZSIDs).toArray();
    for (const mangel of maengel) {
      for (const key in mangel) {
        const omazsKey = key as keyof OMAZS;
        switch (omazsKey) {
          case 'ID':
          case 'OMAZSID':
          case 'OSTAMMID':
          case 'OPRUEFID':
          case 'OMARTID':
          case 'lfNr':
            break;
          default: {
            let value = mangel[omazsKey];
            switch (typeof value) {
              case 'number':
                value = 0;
                break;
              case 'boolean':
                value = false;
                break;
              case 'string':
                value = null;
                break;
            }
            maengel[omazsKey] = value;
          }
        }
      }
      await this.db.omazs.update(mangel.ID, {
        ...mangel,
        FK: -1,
        ...GetDBStatus(),
      });
    } */
  }

  async UpdateToChecklist(req: {
    OTYPID: number;
    OSTAMMID: number;
    OPRUEFID: number;
    typ_Line_Point: number;
    Titel: string;
    maengel?: MangelRowModel[];
  }): Promise<Table<MangelRowType>> {
    const { OTYPID, OSTAMMID, OPRUEFID, typ_Line_Point, Titel } = req;
    let maengel = req.maengel;

    const isChecklist = await this.einstellungenDB.GetTypOption(req.OTYPID, 'Opt_I_CheckList');

    await this.db.transaction('rw', [this.db.kstpvg, this.db.sprue, this.db.omazs, this.db.omzlog], async () => {
      if (!maengel) await this.db.omazs.where({ OPRUEFID }).delete();

      const items = sortBy(await this.db.kstpvg.where({ OTYPID, Titel }).toArray(), 'SortID');

      const existing = lookupBy(maengel, 'Pruefart');

      let lfNr = 1;
      for (const { PruefartID } of items) {
        const art = (await this.db.sprue.get(PruefartID))?.Bezeichnung;
        if (!art) continue;
        const found = existing?.get(art);
        if (found) {
          for (const mangel of found) {
            if (mangel.lfNr != lfNr) {
              await this.db.omazs.where({ OMAZSID: mangel.OMAZSID }).modify({ lfNr });
            }
            lfNr++;
          }
          existing.delete(art);
        } else {
          await this.createMangel({
            OSTAMMID,
            OPRUEFID,
            OTYPID,
            OMARTID: 1,
            Pruefart: art,
            lfNr,
            nIO: false,
            isFullDate: !isChecklist,
          });
          lfNr++;
        }
      }

      for (const nonMatched of existing.values()) {
        for (const mangel of nonMatched) {
          if (mangel.lfNr != lfNr) {
            await this.db.omazs.where({ OMAZSID: mangel.OMAZSID }).modify({ lfNr });
          }
          lfNr++;
        }
      }
    });

    return this.getMaengel({ OSTAMMID, OPRUEFID, OTYPID, typ_Line_Point });
  }

  async DeleteMangel(OMAZSIDs: number[]) {
    await this.db.omazs.where('OMAZSID').anyOf(OMAZSIDs).delete();
  }

  async UpdateMangel(
    mangel: MangelModel,
    medien: FileObject[],
    savedFiles: string
  ): Promise<{ OMAZSID: number; table: Table<MangelRowType> }> {
    const OMAZSID = await this.updateMangel(mangel, medien, savedFiles);
    const table = await this.getMaengel({
      OSTAMMID: mangel.OSTAMMID,
      OPRUEFID: mangel.OPRUEFID,
      OTYPID: mangel.OTYPID,
      typ_Line_Point: mangel.typ_Line_Point,
    });
    return { OMAZSID, table };
  }

  async GetMangelMedia(names: string[]): Promise<MRproResponseInfo<Media>> {
    try {
      const files = await this.sharedDB.GetMediaByNames(names);
      if (!files.length) throw new Error('Auf dem Server sind keine Medien vorhanden');
      return { success: true, files };
    } catch (er) {
      return { success: false, error: (er as Error).message };
    }
  }

  public async IsInspektionVonHeute(OPRUEFID: number): Promise<{ opruef: OPRUEF; vonHeute: boolean }> {
    const opruef = OPRUEFID ? await this.db.opruef.where({ OPRUEFID }).first() : null;
    return { opruef, vonHeute: opruef?.P_Datum == BackendLocaleDatePipe.today };
  }

  private async updateMangel(mangel: MangelModel, medien: FileObject[], savedFiles?: string) {
    const { OSTAMMID, OTYPID, typ_Line_Point, OPRUEFID } = mangel;
    const type = ObjTypePipe.is(typ_Line_Point);

    if (!IsMangelValid(mangel, type)) throw new Error('Mangel ist ungültig');

    return await this.db.transaction(
      'rw',
      [this.db.omazs, this.db.omzlog, this.db.ostamm, this.db.media, this.db.szumz],
      async () => {
        let lfNr = mangel.lfNr ?? 0;
        if (type == 'point') {
          lfNr ??= (await this.db.omazs.where({ OPRUEFID, OSTAMMID, OMARTID: 1 }).lastKey())?.[3 /* lfNr */] + 1;
        }

        const OMAZSID =
          mangel.OMAZSID ??
          (await this.createMangel({ OSTAMMID, OPRUEFID, OTYPID, OMARTID: 1, Pruefart: 'New', lfNr, nIO: true }));
        mangel.nIO ??= false;

        const { GattungID, AAE, AW } =
          mangel.SZUMZID ? await this.db.szumz.get(mangel.SZUMZID) : { GattungID: 0, AAE: 0, AW: 0 };
        const mediaStr = await this.sharedDB.SaveMedia('Mangelbilder', medien, savedFiles, `${OSTAMMID}`);

        await this.db.omazs.where({ OMAZSID }).modify({
          StartMeter: mangel.von,
          EndMeter: mangel.bis,
          Gattung: mangel.Gattung,
          GattungID,
          Stamm: mangel.Stamm,
          Pruefart: mangel.Pruefart,
          nIO: mangel.nIO,
          Lage: mangel.Lage,
          Mangel: mangel.Mangel,
          Instandsetzung: mangel.Instandsetzung,
          Umfang: mangel.Umfang,
          Mengeneinheit: mangel.Mengeneinheit,
          Betriebsgefahr: !!mangel.Betriebsgefahr,
          FK: mangel.nIO ? (mangel.FK ?? 4) : -1,
          Bemerkung: mangel.Bemerkung,
          Bild: mediaStr,
          zuAbt: mangel.Abteilung,
          Equipment: mangel.Equipment,
          ZFeld1: mangel.ZFeld1,
          ZFeld2: mangel.ZFeld2,
          ZFeld3: mangel.ZFeld3,
          ZFeld4: mangel.ZFeld4,
          ZFeld5: mangel.ZFeld5,
          LVPos: mangel.LVPos,
          ProzentFaktor: mangel.Faktor ?? 1,
          FK_Hand: mangel.autoFK != null && mangel.FK != mangel.autoFK,
          SZUMZID: mangel.SZUMZID,
          KAVAAE: AAE,
          KAVAW: AW,
          ...GetDBStatus(),
        });

        return OMAZSID;
      }
    );
  }

  public async createMangel({
    OSTAMMID,
    OPRUEFID,
    OTYPID,
    OMARTID,
    Pruefart,
    lfNr,
    nIO = false,
    isFullDate = true,
  }: {
    OSTAMMID: number;
    OPRUEFID: number;
    OTYPID: number;
    OMARTID: number;
    Pruefart: string;
    lfNr: number;
    nIO?: boolean;
    isFullDate?: boolean;
  }) {
    return await this.db.transaction('rw', [this.db.omazs, this.db.omzlog], async () => {
      const id = await this.db.omazs.add({
        OSTAMMID,
        OPRUEFID,
        Pruefart,
        OMARTID,
        lfNr,
        nIO,
        Auftrag: false,
        Erledigt: false,
        ...GetDBStatus(isFullDate),
      });

      const OMAZSID = +OTYPID.toString().padEnd(9, '0') + id;

      await this.db.omazs.update(id, { OMAZSID });
      await this.db.omzlog.add({ OMAZSID, LogTyp: 'Neu', AuftragNr: 0, ...GetDBStatus() });
      return OMAZSID;
    });
  }

  private async getMaengel({
    OSTAMMID,
    OPRUEFID,
    OTYPID,
    typ_Line_Point,
  }: {
    OSTAMMID: number;
    OPRUEFID: number;
    OTYPID: number;
    typ_Line_Point: number;
  }) {
    let rows: OMAZS[];
    const typ = ObjTypePipe.is(typ_Line_Point);
    const isChecklist = await this.einstellungenDB.GetTypOption(OTYPID, 'Opt_I_CheckList');

    if (typ == 'point') rows = await this.db.omazs.where({ OPRUEFID, OSTAMMID, OMARTID: 1 }).sortBy('lfNr');
    if (typ == 'line')
      rows = await this.db.omazs
        .where({ OPRUEFID, OSTAMMID })
        .and(x => !!x.Mangel && !x.Erledigt && x.OMARTID != 2)
        .sortBy('StartMeter');

    rows.sort((a, b) => {
      if (a.lfNr == b.lfNr) return b.Status.localeCompare(a.Status);
      return 0;
    });
    for (const row of rows) {
      if (row.FK < 0) row.FK = null;
    }
    const cols: MappedColumns<OMAZS, MangelRowType> = ['OMAZSID', 'lfNr'];
    if (typ == 'line') cols.push(['StartMeter', 'von'], ['EndMeter', 'bis'], 'Gattung', 'Stamm');
    cols.push(
      'Pruefart',
      'nIO',
      'Lage',
      'Mangel',
      'Umfang',
      ['ProzentFaktor', 'Faktor'],
      'Mengeneinheit',
      'FK',
      'Instandsetzung',
      'Betriebsgefahr',
      'Equipment',
      ['zuAbt', 'Abteilung'],
      'Bemerkung'
    );
    if (typ == 'point') cols.push('Gattung');
    cols.push(
      ['FK_Hand', 'FK geändert'],
      'SZUMZID',
      'Erledigt',
      ['E_Datum', 'Erledigt am'],
      ['E_Firma', 'Erledigt durch'],
      'Auftrag',
      'Termin',
      'AuftragNr',
      'Auftragnehmer',
      'Bild',
      'SAPExport',
      'UserName',
      'Status'
    );

    const table = AsMappedTableFE<MangelRowType>()(rows, cols);
    if (isChecklist) {
      insertBeforeBy(table.columns, 'id', 'nIO', { id: 'iO' });
      for (const row of table.rows) {
        row.iO = !row.nIO && compareDate(row.Status, '>', 'ms', BackendLocaleDatePipe.today);
      }
    }

    return table;
  }

  private async getInHistorie(OMAZSID: number): Promise<Row> {
    const row = await this.db.omazs.where({ OMAZSID }).first();
    return AsMappedRow()(row, [
      'OMAZSID',
      ['OSTAMMID', 'ostammid'],
      'lfNr',
      ['StartMeter', 'von'],
      ['EndMeter', 'bis'],
      ['Gattung', 'gattung'],
      ['Stamm', 'stamm'],
      ['Pruefart', 'pruefart'],
      ['Mangel', 'mangel'],
      ['Lage', 'lage'],
      ['Umfang', 'umfang'],
      ['Mengeneinheit', 'mengeneinheit'],
      ['FK', 'fehlerklasse'],
      ['Instandsetzung', 'instandsetzungshinweis'],
      ['Betriebsgefahr', 'betriebsgefahr'],
      ['Bild', 'bildpfad'],
      ['Erledigt', 'erledigtStatus'],
      ['E_Datum', 'erledigt_am'],
      ['E_Firma', 'erledigt_durch'],
      ['InstPic1', 'rueckbilder'],
      ['ProzentFaktor', 'faktor'],
      ['FK_Hand', 'fk_aenderung'],
      ['GattungID', 'gattung_id'],
      ['KAVAAE', 'abnutzungsaequivalent'],
      ['KAVAW', 'anteilige_wichtung'],
      ['EP', 'ep'],
      ['Auftrag', 'auftrag'],
      ['Termin', 'termin'],
      ['AuftragNr', 'auftrag_nr'],
      ['Auftragsart', 'auftragsart'],
      ['Auftragnehmer', 'auftragnehmer'],
      ['SAPExport', 'sap_export'],
      ['MELDSTAT', 'meldstat'],
      ['MELDNR', 'meld_nr'],
      ['AUFSTAT', 'aufstat'],
      ['Equipment', 'equipment'],
      ['Faktor', 'faktor1'],
      ['Bemerkung', 'bemerkung'],
      ['UserName', 'username'],
      ['Status', 'status'],
    ]);
  }

  private async createNewPruefung(
    OSTAMMID: number,
    opruef: OPRUEF,
    KPRTYPID: number,
    OTYPID: number,
    typ_Line_Point: number,
    isFullDate: boolean
  ): Promise<number> {
    return await this.db.transaction(
      'rw',
      [this.db.opruef, this.db.omazs, this.db.ostamm, this.db.omzlog],
      async () => {
        const old_OPRUEFID = opruef?.OPRUEFID ?? 0;

        opruef ??= {} as OPRUEF;
        delete opruef.ID;
        delete opruef.OPRUEFID;

        const id = await this.db.opruef.add({
          ...opruef,
          KPRTYPID,
          OSTAMMID,
          P_DelStatus: 0,
          P_IntStatus: 'Hauptprüfung',
          P_Datum: BackendLocaleDatePipe.today,
          P_EBL_Status: 0,
          P_EBL_Checked: 0,
          P_EBL_CheckDate: null,
          P_EBL_Name: null,
          P_ALV_Status: 0,
          P_ALV_Checked: 0,
          P_ALV_CheckDate: null,
          P_ALV_Name: null,
          ...GetDBStatus(),
        });
        const OPRUEFID = +OTYPID.toString().padEnd(7, '0') + id;
        await this.db.opruef.update(id, { OPRUEFID });

        //#region LPID
        const lpid_field = GetLPIDFieldBy(KPRTYPID);
        const lpdatum_field = GetLPDatumFieldBy(KPRTYPID);
        await this.db.ostamm.where({ OSTAMMID }).modify({
          [lpid_field]: OPRUEFID,
          [lpdatum_field]: BackendLocaleDatePipe.today,
        });
        //#endregion

        //#region Copy Mängel
        const type = ObjTypePipe.is(typ_Line_Point);
        const maengel = await this.db.omazs
          .where({ OPRUEFID: old_OPRUEFID })
          .and(x => (type == 'point' ? x.OMARTID == 1 : !x.Erledigt))
          .toArray();

        for (const mangel of maengel) {
          const OMAZSID = await this.createMangel({
            OSTAMMID,
            OPRUEFID,
            OTYPID,
            OMARTID: 1,
            Pruefart: mangel.Pruefart,
            lfNr: mangel.lfNr,
            nIO: mangel.nIO,
          });
          delete mangel.ID;
          delete mangel.OPRUEFID;
          await this.db.omazs
            .where({ OMAZSID })
            .modify(
              !mangel.Mangel || mangel.Erledigt ? { FK: -1, nIO: false, ...GetDBStatus(isFullDate) } : { ...mangel }
            );
        }
        //#endregion
        return OPRUEFID;
      }
    );
  }

  private async resetLPID(OSTAMMID: number, KPRUVID: number) {
    const LPIDField = GetLPIDFieldBy(KPRUVID);
    const LPDatumField = GetLPDatumFieldBy(KPRUVID);

    let maxKey = await this.db.opruef
      .where('[OSTAMMID+P_IntStatus+KPRTYPID+P_DelStatus+P_Datum+OPRUEFID]')
      .between(
        [OSTAMMID, 'Hauptprüfung', KPRUVID, 0, Dexie.minKey, Dexie.minKey],
        [OSTAMMID, 'Hauptprüfung', KPRUVID, 0, Dexie.maxKey, Dexie.maxKey]
      )
      .lastKey();
    const [, , , , P_Datum, OPRUEFID] = (maxKey ?? []) as unknown[];
    await this.db.ostamm.where({ OSTAMMID }).modify({
      [LPIDField]: OPRUEFID,
      [LPDatumField]: P_Datum,
    });
  }
}

export interface OMAZS extends DBStatus {
  ID?: number;
  OMAZSID?: number;
  OSTAMMID?: number;
  OPRUEFID?: number;
  OMARTID?: number;
  lfNr?: number;
  StartMeter?: number;
  EndMeter?: number;
  Strasse?: string;
  Richtung?: number;
  Haus?: string;
  HNummer?: string;
  LaengeInMeter?: number;
  Gattung?: string;
  Stamm?: string;
  Pruefart?: string;
  Mangel?: string;
  Lage?: string;
  Umfang?: number;
  Mengeneinheit?: string;
  FK?: number;
  Instandsetzung?: string;
  Betriebsgefahr?: boolean;
  Bild?: string;
  Erledigt: boolean;
  E_Datum?: string;
  E_Firma?: string;
  ProzentFaktor?: number;
  FK_Hand?: boolean;
  Auftrag: boolean;
  Termin?: string;
  AuftragNr?: string;
  Auftragnehmer?: string;
  Auftragsart?: string;
  Rueckmeldetext?: string;
  GattungID?: number;
  KAVAAE?: number;
  KAVAW?: number;
  ParentID?: number;
  Bemerkung?: string;
  Kosten?: number;
  Stunden?: number;
  Verantwortlich?: string;
  SAPExport?: boolean;
  InstPic1?: string;
  InstPic2?: string;
  InstPic3?: string;
  EP?: number;
  GPS_Lat?: string;
  GPS_Lon?: string;
  GPS_Image?: string;
  Kostenart?: string;
  MELDNR?: string;
  MELDSTAT?: string;
  AUFSTAT?: string;
  ZEDASID?: string;
  ZEDASERL?: boolean;
  ZuOID?: number;
  nIO: boolean;
  Faktor?: number;
  Meldender?: string;
  UrsacheGrund?: string;
  SperrID?: number;
  SperrStatus?: number;
  GewaehrZeit?: number;
  M_Datum?: string;
  M_Zeit?: string;
  StoerArt?: string;
  optUrsacheGrund?: string;
  IntAuftragNr?: number;
  LVPos?: string;
  zuAbt?: string;
  SAPAusfall?: boolean;
  SAPEckende?: string;
  SAPEckendeNeu?: string;
  S?: number;
  V?: number;
  D?: number;
  SZUMZID?: number;
  Teilerneuerung?: string;
  Ex_Infralife?: boolean;
  UpdateZEDAS?: boolean;
  Angebot?: boolean;
  Equipment?: string;
  altInstandsetzung?: string;
  altUmfang?: number;
  altMEH?: string;
  altVon?: number;
  altBis?: number;
  ZFeld1?: string;
  ZFeld2?: string;
  ZFeld3?: string;
  ZFeld4?: string;
  ZFeld5?: string;
}

const EMPTY_MANGEL = {
  StartMeter: 0,
  EndMeter: 0,
  Strasse: null,
  Richtung: 0,
  Haus: null,
  HNummer: null,
  LaengeInMeter: 0,
  Gattung: null,
  Stamm: null,
  Mangel: null,
  Lage: null,
  Umfang: null,
  Mengeneinheit: null,
  Instandsetzung: null,
  FK: -1,
  Betriebsgefahr: false,
  Bild: null,
  Erledigt: false,
  E_Datum: null,
  E_Firma: null,
  ProzentFaktor: 0,
  FK_Hand: false,
  Auftrag: false,
  Termin: null,
  AuftragNr: null,
  Auftragnehmer: null,
  Auftragsart: null,
  Rueckmeldetext: null,
  GattungID: 0,
  KAVAAE: 0,
  KAVAW: 0,
  ParentID: 0,
  Bemerkung: null,
  Kosten: 0,
  Stunden: 0,
  Verantwortlich: null,
  SAPExport: false,
  InstPic1: null,
  InstPic2: null,
  InstPic3: null,
  EP: 0,
  GPS_Lat: null,
  GPS_Lon: null,
  GPS_Image: null,
  Kostenart: null,
  MELDNR: null,
  MELDSTAT: null,
  AUFSTAT: null,
  ZEDASID: null,
  ZEDASERL: null,
  ZuOID: 0,
  nIO: false,
  Faktor: 0,
  Meldender: null,
  UrsacheGrund: null,
  SperrID: 0,
  SperrStatus: 0,
  GewaehrZeit: 0,
  M_Datum: null,
  M_Zeit: null,
  StoerArt: null,
  optUrsacheGrund: null,
  IntAuftragNr: 0,
  LVPos: null,
  zuAbt: null,
  SAPAusfall: false,
  SAPEckende: null,
  SAPEckendeNeu: null,
  S: 0,
  V: 0,
  D: 0,
  SZUMZID: 0,
  Teilerneuerung: null,
  Ex_Infralife: false,
  UpdateZEDAS: false,
  Angebot: null,
  Equipment: null,
  altInstandsetzung: null,
  altUmfang: null,
  altMEH: null,
  altVon: 0,
  altBis: 0,
  ZFeld1: null,
  ZFeld2: null,
  ZFeld3: null,
  ZFeld4: null,
  ZFeld5: null,
} as const satisfies { [Key in keyof OMAZS]?: OMAZS[Key] };
